import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { extractErrorMessage } from '@core/http/api-response.envelope';
import { ToastService } from '@app/shared/components/toast/services/toast.service';

import { AuthService } from '../../../services/auth.service';
import { TokenStorageService } from '../../../services/token-storage.service';
import {
  SubmitCredentialResponse,
  VerifyOtpResponse,
} from '../../../models/auth.model';
import {
  submitCredentials,
  submitCredentialsSuccess,
  submitCredentialsFailure,
  verifyOtp,
  verifyOtpSuccess,
  verifyOtpFailure,
  verifyGoogle,
  verifyGoogleSuccess,
  verifyGoogleFailure,
  startGoogleSetup,
  startGoogleSetupSuccess,
  startGoogleSetupFailure,
  googleAuthStepEntered,
  resendOtp,
  resendOtpSuccess,
  resendOtpFailure,
  setError,
  backToCredentials,
} from './login.actions';
import {
  selectSessionToken,
  selectUsername,
  selectPassword,
} from './login.selectors';
import {
  LOGIN_STEP_ROUTE,
  isGoogleAuthStep,
  parseLoginStep,
} from './login-step';

@Injectable()
export class LoginEffects {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  submitCredentials$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitCredentials),
      switchMap(({ payload }) =>
        this.authService.login(payload).pipe(
          map((res: SubmitCredentialResponse) => {
            const data = res?.data;
            // A step the app cannot read — `COMPLETE` included: a sign-in
            // does not finish without its second factor — is refused, not
            // guessed (F-033)
            const nextStep = parseLoginStep(data?.nextStep);
            if (!nextStep) {
              return this.unrecognisedStep();
            }
            return submitCredentialsSuccess({
              username: payload.phone,
              sessionToken: data?.token ?? null,
              loginMethod: data?.loginMethod ?? null,
              password: payload.password,
              nextStep,
              googleAuthRequired: isGoogleAuthStep(nextStep),
            });
          }),
          catchError((err: any) =>
            of(
              submitCredentialsFailure({
                error: this.normalizeApiError(err),
              })
            )
          )
        )
      )
    )
  );

  submitCredentialsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(submitCredentialsSuccess),
        map(({ nextStep }) => this.navigateTo(nextStep))
      ),
    { dispatch: false }
  );

  verifyOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyOtp),
      withLatestFrom(this.store.select(selectSessionToken)),
      switchMap(([{ code }, sessionToken]) => {
        if (!sessionToken) {
          return of(
            setError({
              error: this.translate.instant('auth.login.errors.missingSession'),
            })
          );
        }
        return this.authService
          .verifyOtp({ activationToken: sessionToken, otpCode: code })
          .pipe(
            map((res: VerifyOtpResponse) => {
              const data = res?.data;
              const nextStepRaw = data?.nextStep ?? '';
              const nextStepUpper = nextStepRaw?.toUpperCase?.() ?? '';

              if (
                nextStepUpper === 'COMPLETE' ||
                nextStepUpper === 'COMPLETED'
              ) {
                const accessToken = data?.token ?? null;
                const refreshToken = data?.refreshToken ?? null;
                if (!accessToken || !refreshToken) {
                  return setError({
                    error: this.translate.instant(
                      'auth.login.errors.missingTokens'
                    ),
                  });
                }
                this.tokenStorage.save(accessToken, refreshToken);
                this.router.navigate(['/dashboard/account-settings']);
                return verifyOtpSuccess({
                  sessionToken: null,
                  loginMethod: null,
                  nextStep: null,
                  googleAuthRequired: false,
                  isComplete: true,
                  accessToken,
                  refreshToken,
                });
              }

              const nextStep = parseLoginStep(nextStepRaw);
              if (!nextStep) {
                return this.unrecognisedStep();
              }
              return verifyOtpSuccess({
                sessionToken: data?.token ?? sessionToken,
                loginMethod: data?.loginMethod ?? null,
                nextStep,
                googleAuthRequired: isGoogleAuthStep(nextStep),
                isComplete: false,
              });
            }),
            catchError((err: any) =>
              of(verifyOtpFailure({ error: this.normalizeApiError(err) }))
            )
          );
      })
    )
  );

  verifyOtpSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(verifyOtpSuccess),
        map(({ nextStep, isComplete }) => {
          if (!isComplete) {
            this.navigateTo(nextStep);
          }
        })
      ),
    { dispatch: false }
  );

  verifyGoogle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyGoogle),
      withLatestFrom(this.store.select(selectSessionToken)),
      switchMap(([{ code }, sessionToken]) => {
        if (!sessionToken) {
          return of(
            setError({
              error: this.translate.instant('auth.login.errors.expiredSession'),
            })
          );
        }
        return this.authService
          .verifyGoogleAuth({ sessionToken, googleAuthCode: code })
          .pipe(
            map((res: VerifyOtpResponse) => {
              const data = res?.data;
              const accessToken = data?.token ?? null;
              const refreshToken = data?.refreshToken ?? null;
              if (!accessToken || !refreshToken) {
                return setError({
                  error: this.translate.instant(
                    'auth.login.errors.missingTokens'
                  ),
                });
              }
              this.tokenStorage.save(accessToken, refreshToken);
              this.router.navigate(['/dashboard/account-settings']);
              return verifyGoogleSuccess({
                accessToken,
                refreshToken,
              });
            }),
            catchError((err: any) =>
              of(verifyGoogleFailure({ error: this.normalizeApiError(err) }))
            )
          );
      })
    )
  );

  startGoogleSetup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startGoogleSetup),
      withLatestFrom(this.store.select(selectSessionToken)),
      switchMap(([, sessionToken]) => {
        if (!sessionToken) {
          return of(
            setError({
              error: this.translate.instant('auth.login.errors.expiredSession'),
            })
          );
        }
        return this.authService.startLoginGoogleAuthSetup(sessionToken).pipe(
          map(res => {
            const setup = res?.data;
            if (!setup?.qrCodeUrl) {
              return setError({
                error: this.translate.instant('auth.login.errors.generic'),
              });
            }
            return startGoogleSetupSuccess({ setup });
          }),
          catchError((err: unknown) =>
            of(startGoogleSetupFailure({ error: this.normalizeApiError(err) }))
          )
        );
      })
    )
  );

  googleAuthStepEntered$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(googleAuthStepEntered),
        map(({ setupRequired }) =>
          this.navigateTo(setupRequired ? 'SETUP_2FA' : 'VERIFY_2FA')
        )
      ),
    { dispatch: false }
  );

  resendOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resendOtp),
      withLatestFrom(
        this.store.select(selectUsername),
        this.store.select(selectPassword)
      ),
      switchMap(([, username, password]) => {
        if (!username || !password) {
          return of(
            setError({
              error: this.translate.instant(
                'auth.login.errors.missingCredentials'
              ),
            })
          );
        }
        return this.authService.login({ phone: username, password }).pipe(
          map((res: SubmitCredentialResponse) => {
            const data = res?.data;
            const nextStep = parseLoginStep(data?.nextStep);
            if (!nextStep) {
              return this.unrecognisedStep();
            }
            return resendOtpSuccess({
              sessionToken: data?.token ?? null,
              loginMethod: data?.loginMethod ?? null,
              nextStep,
              googleAuthRequired: isGoogleAuthStep(nextStep),
            });
          }),
          catchError((err: any) =>
            of(resendOtpFailure({ error: this.normalizeApiError(err) }))
          )
        );
      })
    )
  );

  resendOtpSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(resendOtpSuccess),
        map(({ nextStep }) => this.navigateTo(nextStep))
      ),
    { dispatch: false }
  );

  backToCredentials$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(backToCredentials),
        map(() => {
          this.router.navigate(['/auth/login/credentials']);
        })
      ),
    { dispatch: false }
  );

  /**
   * The flow's own refusals (a guard clause, a token-less completion, a step
   * the app cannot read) send no request or answer a 200, so the interceptor
   * says nothing, and neither the credentials nor the code screen renders
   * `state.error` — so they are toasted here, as in activation and recovery.
   */
  refusalToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setError),
        tap(({ error }) => {
          if (error) this.toast.error(error);
        })
      ),
    { dispatch: false }
  );

  private unrecognisedStep() {
    return setError({
      error: this.translate.instant('auth.login.errors.unrecognisedStep'),
    });
  }

  private navigateTo(nextStep: string | null): void {
    const step = parseLoginStep(nextStep);
    if (step) {
      this.router.navigate(LOGIN_STEP_ROUTE[step]);
    }
  }

  private normalizeApiError(err: unknown): string {
    return extractErrorMessage(
      err,
      this.translate.instant('auth.login.errors.generic')
    );
  }
}
